/**
 * UndoButton — 5-second window after a right-swipe (approve).
 *
 * Per plan §6.1: visible immediately after commit, hides after the
 * server-issued 5s undo window expires. Tap = call `onUndo()` which
 * POSTs `/v1/daily/:child_id/undo` and rewinds the local swiper
 * index.
 *
 * The countdown is driven by `expiresAt` (server-issued ISO timestamp)
 * rather than a 5-second timer, so the client and server share the
 * same deadline — if the network is slow, the client still respects
 * the server's 410 Gone envelope.
 */

import { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text } from 'react-native';

import { Brand, Radii } from '@/constants/theme';

export type UndoButtonProps = {
  /**
   * Server-issued ISO timestamp for when the undo window expires.
   * When `null`, the button is hidden.
   */
  expiresAt: string | null;
  /** Tap handler — call postUndo + rewind local state. */
  onUndo: () => void;
  /** Disable while the network call is in flight. */
  loading?: boolean;
};

const TICK_MS = 250;

function secondsRemaining(expiresAt: string): number {
  const ms = Date.parse(expiresAt) - Date.now();
  return Math.max(0, Math.ceil(ms / 1000));
}

export function UndoButton({ expiresAt, onUndo, loading = false }: UndoButtonProps) {
  const [remaining, setRemaining] = useState<number | null>(
    expiresAt ? secondsRemaining(expiresAt) : null,
  );

  useEffect(() => {
    if (!expiresAt) {
      setRemaining(null);
      return;
    }
    setRemaining(secondsRemaining(expiresAt));
    const id = setInterval(() => {
      const r = secondsRemaining(expiresAt);
      setRemaining(r);
      if (r <= 0) clearInterval(id);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (remaining === null || remaining <= 0) return null;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={loading ? 'Membatalkan swipe' : `Batalkan swipe, ${remaining} detik tersisa`}
      onPress={onUndo}
      disabled={loading}
      style={({ pressed }) => [styles.btn, pressed && styles.pressed, loading && styles.loading]}
    >
      {loading ? <ActivityIndicator size="small" color="#ffffff" /> : <Text style={styles.text}>↶ Batal ({remaining})</Text>}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  btn: {
    backgroundColor: Brand.primary,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: Radii.pill,
    minHeight: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  pressed: { opacity: 0.75 },
  loading: { opacity: 0.5 },
});
